import * as Sentry from '@sentry/react';
import { buildHeaders, handleApiError } from './CommonService.Functions';

/**
 * La API de Offices (turnero) existe hoy en dos versiones dentro del repo IchtysOffice.API:
 * la legacy (`turnero/`) envuelve todo en `Response<T>` ({ succeeded, message, errors, data }) y la
 * nueva (`IchtysOffice.API/`) devuelve el DTO tal cual. Este helper acepta las dos formas para que la
 * pantalla no quede vacía cuando se cambie el backend desplegado.
 */
export const unwrapOfficesBody = <T>(body: unknown): T => {
    if (
        body !== null &&
        typeof body === 'object' &&
        !Array.isArray(body) &&
        'data' in (body as Record<string, unknown>) &&
        'succeeded' in (body as Record<string, unknown>)
    ) {
        return (body as { data: T }).data;
    }
    return body as T;
};

/** Igual que `apiRequest`, pero tolera respuestas envueltas o crudas y cuerpos vacíos (200/204). */
export const officesApiRequest = async <T>(
    url: string,
    token: string,
    context: string,
    options: RequestInit = {}
): Promise<T> => {
    const requestOptions: RequestInit = {
        ...options,
        headers: buildHeaders(token, options.headers as Record<string, string>)
    };

    try {
        const response = await fetch(url, requestOptions);

        if (!response.ok) {
            Sentry.captureException(response);
            throw response;
        }

        if (response.status === 204) {
            return undefined as unknown as T;
        }

        const text = typeof response.text === 'function' ? await response.text() : '';
        if (!text || !text.trim()) {
            return undefined as unknown as T;
        }

        return unwrapOfficesBody<T>(JSON.parse(text));
    } catch (error) {
        throw await handleApiError(error, context);
    }
};
